/**
 * The openid scope.
 */
const OPEN_ID_SCOPE = 'openid'

/**
 * The openid scope.
 */
const PROFILE_SCOPE = 'profile'

/**
 * The openid scope.
 */
const EMAIL_SCOPE = 'email'

/**
 * The scope for getting location data from Museum.
 * Used to determine visibility of the permits tab and change log tab in location management.
 * Used by routes: `/v2/locations/{locationId}`, `/v1/locations/{locationId}/permits/history`,
 * `/v1/search`, `/v1/locations/{locationId}/deltas`.
 */
export const READ_LOCATIONS_SCOPE = 'https://museum.mon.simplisafe.com/locations:read'

/**
 * The scope for seeing the Engagement Review Tool applet
 */
export const ERT_SCOPE = 'https://art.mon.simplisafe.com/features:engagement-review'

/**
 * The scope to access ERT Inspect mode
 */
export const ERT_INSPECT_MODE_SCOPE = 'https://art.mon.simplisafe.com/features:engagement-review:inspect-mode'

/**
 * The scope to access ERT QA mode
 */
export const ERT_QA_MODE_SCOPE = 'https://art.mon.simplisafe.com/features:engagement-review:qa-mode'

/**
 * The scope to access ERT Customer Care mode
 */
export const ERT_CUSTOMER_CARE_SCOPE = 'https://art.mon.simplisafe.com/features:engagement-review:customer-care'

/**
 * The scope for seeing the Engagement Review Tool applet
 */
export const ERT_READ_ENGAGEMENT_RECORDINGS = 'https://monitoring-metrics.services.simplisafe.com/read:engagement-recordings'

/**
 * The scope for getting alarm data from Museum.
 * Used by routes: `/v1/locations/{locationId}/alarms`, `/v1/locations/{locationId}/alarms/{alarmId}`.
 */
export const READ_ALARMS_SCOPE = 'https://museum.mon.simplisafe.com/locations:alarms:read'

/**
 * The scope for posting metrics from Museum.
 * Used by route: `/v1/metrics`.
 */
export const WRITE_METRICS_SCOPE = 'https://museum.mon.simplisafe.com/metrics:write'

/**
 * The scope for getting MSP location data from Museum.
 * Not used in the UI.
 * Used by routes: `/v1/monitoring-service-providers/{monitoringServiceProviderId}/locations/{transmitterCode}`,
 * `/v1/monitoring-service-provider-locations/{locationId}`.
 */
export const READ_MSP_LOCATIONS_SCOPE = 'https://museum.mon.simplisafe.com/monitoring-service-providers:locations:read'

/**
 * The scope for getting alarm recording data from Museum.
 * Used by routes: `/v1/locations/{locationId}/engagements/{engagementId}/token`,
 * `/v1/locations/{locationId}/engagements/{engagementId}/recordings/cameras/{cameraId}`.
 * Also affects `/v1/locations/{locationId}/alarms/{alarmId}` when recording data is available.
 */
export const READ_ALARM_RECORDINGS_SCOPE = 'https://api.monitoring.simplisafe.com/locations:alarms:recordings:read'

/**
 * The scope for getting alarm SMS incidents data from Partyline.
 */
export const READ_ALARM_SMS_INCIDENTS_SCOPE = 'https://partyline.mon.simplisafe.com/read:incidents'

/**
 * The scope for writing locations data to Museum.
 * Used by route: `/v1/locations/{locationId}/permits/{agencyType}`.
 */
export const WRITE_LOCATIONS_SCOPE = 'https://museum.mon.simplisafe.com/locations:write'

/**
 * The scope for reading feature data for a location from Museum.
 * Used by route: `/v1/locations/{locationId}`.
 */
export const READ_LOCATION_FEATURES_SCOPE = 'https://museum.simplisafe.com/locations:features:read'

/**
 * The scope for reading permit data from Museum.
 * Used by route: `/v1/locations/{locationId}/permits`.
 */
export const READ_PERMITS_SCOPE = 'https://museum.simplisafe.com/locations:permits:read'

/**
 * The scope for writing permit data to Museum.
 * Used by route: `/v1/locations/{locationId}/permits/{agencyType}`.
 */
export const WRITE_PERMITS_SCOPE = 'https://museum.simplisafe.com/locations:permits:write'

/**
 * The scope for getting permitting comments data from Museum.
 * Used to determine visibility of the permitting comments under the permits tab in location management.
 * Used by route: `/v1/locations/{locationId}/permitting-comments`.
 */
export const READ_PERMITTING_COMMENTS_SCOPE = 'https://museum.mon.simplisafe.com/read:locations:permitting-comments'

/**
 * The scope for writing permitting comments to Museum.
 * Controls the add comment button in the UI.
 * Used by route: `/v1/locations/{locationId}/permitting-comments`.
 */
export const WRITE_PERMITTING_COMMENTS_SCOPE = 'https://museum.mon.simplisafe.com/write:locations:permitting-comments'

/**
 * The scope for reading jurisdiction rules from Museum.
 * Used to read the jurisdiction details in location management
 */
export const READ_JURISDICTION_RULES_SCOPE = 'https://museum.mon.simplisafe.com/locations:jurisdiction-rules:read'

/**
 * The scope for accessing the jurisdiction rules tab.
 * Used to determine visibility of the jurisdiction details tab in location management
 */
export const JURISDICTION_RULES_TAB_SCOPE = 'https://museum.mon.simplisafe.com/locations:jurisdiction-rules:tab'

/**
 * The scope for reading device data relating to location from Museum.
 * Used by route: `/v1/locations/{locationId}/cameras`.
 */
export const READ_DEVICES_SCOPE = 'https://museum.services.simplisafe.com/locations:devices:read'

/**
 * The scope for reading engagement data from Museum.
 * Used by routes: `/v1/engagements`, `/v1/engagements/{engagementId}`, `/v1/engagements/{engagementId}/activities`.
 */
export const READ_ENGAGEMENTS_SCOPE = 'https://museum.services.simplisafe.com/engagements:read'

/**
 * The scope for reading moderation events from Museum.
 * Used by route: `/v1/locations/{locationId}/moderation-events`.
 */
export const READ_MODERATION_EVENTS_SCOPE = 'https://museum.services.simplisafe.com/moderation-event:read'

/**
 * The scope for writing moderation events to Museum.
 * Used by routes: `/v1/locations/{locationId}/moderation-events`, `/v1/locations/{locationId}/moderation-events/{eventId}`.
 */
export const WRITE_MODERATION_EVENTS_SCOPE = 'https://museum.services.simplisafe.com/moderation-event:write'

/**
 * The scope for reading primary and secondary contacts for a location from Museum.
 * Not used in the UI.
 */
export const READ_LOCATION_CONTACTS_SCOPE = 'https://museum.simplisafe.com/locations:contacts:read'

/**
 * The scope for writing primary and secondary contacts for a location from Museum.
 * Controls the edit button for the primary and secondary contact fields in location settings.
 * Used by route: `/v1/locations/{locationId}/contacts`.
 */
export const WRITE_LOCATION_CONTACTS_SCOPE = 'https://museum.simplisafe.com/locations:contacts:write'

/**
 * The scope for reading the address fields for a location from Museum.
 * Not used in the UI.
 */
export const READ_LOCATION_ADDRESS_SCOPE = 'https://museum.simplisafe.com/locations:address:read'

/**
 * The scope for writing the address fields for a location from Museum.
 * Controls the edit button for the address fields in location settings.
 * Used by route: `/v1/locations/{locationId}/address`.
 */
export const WRITE_LOCATION_ADDRESS_SCOPE = 'https://museum.simplisafe.com/locations:address:write'

/**
 * The scope for writing the template ID for a location from Museum.
 * Controls the ability to edit the template ID field in location settings.
 * Used by route: `/v1/locations/{locationId}/template-id`.
 */
export const WRITE_LOCATION_TEMPLATE_ID_SCOPE = 'https://museum.simplisafe.com/locations:template-id:write'

/**
 * The scope for writing the dispatch notes for a location from Museum.
 * Controls the edit button for the dispatch notes field in location settings.
 * Used by route: `/v1/locations/{locationId}/dispatch-notes`.
 */
export const WRITE_LOCATION_DISPATCH_NOTES_SCOPE = 'https://museum.simplisafe.com/locations:dispatch-notes:write'

/**
 * The scope for reading the monitoring data for a location from Museum.
 * Used to show the practice mode tab in location management.
 */
export const READ_LOCATION_MONITORING_SCOPE = 'https://museum.simplisafe.com/locations:monitoring:read'

/**
 * The scope for writing the monitoring data for a location from Museum.
 * Controls the start and stop practice mode buttons in the practice mode tab.
 * Used by route: `/v1/locations/{locationId}/monitoring`.
 */
export const WRITE_LOCATION_MONITORING_SCOPE = 'https://museum.simplisafe.com/locations:monitoring:write'

/**
 * The scope for reading the safeword for a location from Museum.
 * Not used in the UI.
 * Used by route: `/v1/locations/{locationId}/safe-words`.
 */
export const READ_LOCATION_SAFEWORD_SCOPE = 'https://museum.simplisafe.com/locations:safe-words:read'

/**
 * The scope for writing the safeword for a location from Museum.
 * Controls the ability to edit the safeword field in location settings.
 * Used by route: `/v1/locations/{locationId}/safe-words/{safeWordId}`.
 */
export const WRITE_LOCATION_SAFEWORD_SCOPE = 'https://museum.simplisafe.com/locations:safe-words:write'

/**
 * The scope for the ability to send the monitoring certificate for a location from Museum.
 * Controls the send monitoring certificate button in the practice mode tab (but not the view certificate button).
 * Used by routes: `/v1/locations/{locationId}/certificates/monitoring`,
 * `/v1/locations/{locationId}/certificates/monitoring/email`.
 */
export const WRITE_MONITORING_CERTIFICATE_SCOPE = 'https://museum.simplisafe.com/access:monitoring-certificates'

/**
 * The scope for validating an address through Museum.
 * Used by route: `/v1/validation/validate-address`.
 */
export const VALIDATE_ADDRESS_SCOPE = 'https://museum.mon.simplisafe.com/validate:address'

/**
 * The scope for reading the msp identifiers and site number for a location from Museum.
 * Not used in the UI.
 * Used by route: `/v1/monitoring-service-provider-locations/{locationId}/identifiers`.
 */
export const READ_LOCATION_MSP_IDENTIFIERS_SCOPE = 'https://museum.simplisafe.com/locations:msp-identifiers:read'

/**
 * The scope for writing the msp identifiers and site number for a location from Museum.
 * Controls the ability to edit the site number field in location settings.
 * Used by route: `/v1/monitoring-service-provider-locations/{locationId}/identifiers`.
 */
export const WRITE_LOCATION_MSP_IDENTIFIERS_SCOPE = 'https://museum.simplisafe.com/locations:msp-identifiers:write'

/**
 * The scope for reading specialist data from Museum.
 * Used by route: `/v1/specialists/{monitoringServiceProviderId}/{specialistId}`.
 */
export const READ_SPECIALISTS_SCOPE = 'https://museum.simplisafe.com/specialists:read'

/**
 * The scope for viewing workflow data for a location from Museum.
 * Not used in the UI.
 * Used by route: `/v1/locations/{locationId}/workflows`.
 */
export const READ_LOCATION_WORKFLOW_SCOPE = 'https://museum.simplisafe.com/location-workflow:read'

const FAKE_SCOPE_FOR_TESTING = 'https://museum.services.simplisafe.com/fake:scope:for-testing'

/**
 * The scope for reading location changes.
 * Used by route: `/v1/locations/{locationId}/changes`.
 */
export const READ_LOCATION_CHANGE_SCOPE = 'https://museum.services.simplisafe.com/locations:changes:read'

/**
 * The scope for writing location changes.
 * Used by routes: `/v1/locations/{locationId}/changes`, `/v1/locations/{locationId}/changes/{locationChangeId}`.
 */
export const WRITE_LOCATION_CHANGE_SCOPE = 'https://museum.services.simplisafe.com/locations:changes:write'

/**
 * The scope for deleting location changes.
 */
export const DELETE_LOCATION_CHANGE_SCOPE = 'https://museum.services.simplisafe.com/locations:changes:delete'

/**
 * The scope for access to the developer tools applet
 */
export const DEVTOOLS_SCOPE = 'https://art.mon.simplisafe.com/devtools'

export const ALL_LOCATION_CHANGE_SCOPES = [
  READ_LOCATION_CHANGE_SCOPE,
  WRITE_LOCATION_CHANGE_SCOPE,
  DELETE_LOCATION_CHANGE_SCOPE,
  VALIDATE_ADDRESS_SCOPE
]

// Alarm Review scopes
export const READ_ALARM_REVIEW_SCOPES = [READ_LOCATIONS_SCOPE, READ_ALARMS_SCOPE, READ_ALARM_RECORDINGS_SCOPE]
export const WRITE_ALARM_REVIEW_SCOPES = [WRITE_LOCATIONS_SCOPE, WRITE_LOCATIONS_SCOPE, WRITE_METRICS_SCOPE]
export const ALL_ALARM_REVIEW_SCOPES = [...READ_ALARM_REVIEW_SCOPES, ...WRITE_ALARM_REVIEW_SCOPES, READ_MSP_LOCATIONS_SCOPE]

// Alarm SMS Review scopes
export const READ_ALARM_SMS_REVIEW_SCOPES = [READ_LOCATIONS_SCOPE, READ_ALARMS_SCOPE, READ_ALARM_SMS_INCIDENTS_SCOPE]

// PermitsTab scopes
export const READ_PERMITTING_SCOPES = [READ_LOCATIONS_SCOPE, READ_PERMITTING_COMMENTS_SCOPE, READ_PERMITS_SCOPE, READ_LOCATION_ADDRESS_SCOPE]
export const WRITE_PERMITTING_SCOPES = [WRITE_LOCATIONS_SCOPE, WRITE_PERMITTING_COMMENTS_SCOPE, WRITE_PERMITS_SCOPE, WRITE_METRICS_SCOPE]
export const ALL_PERMITTING_SCOPES = [...READ_PERMITTING_SCOPES, ...WRITE_PERMITTING_SCOPES, READ_MSP_LOCATIONS_SCOPE]

// Monitoring Moderation scopes
export const READ_MONITORING_MODERATION_SCOPES = [READ_LOCATIONS_SCOPE, READ_DEVICES_SCOPE, READ_MODERATION_EVENTS_SCOPE]
export const WRITE_MONITORING_MODERATION_SCOPES = [WRITE_MODERATION_EVENTS_SCOPE]
export const ALL_MONITORING_MODERATION_SCOPES = [...READ_MONITORING_MODERATION_SCOPES, ...WRITE_MONITORING_MODERATION_SCOPES]

// Location Management scopes
export const READ_LOCATION_MANAGEMENT_SETTINGS_SCOPES = [
  READ_LOCATIONS_SCOPE, // gives access to the permits tab, change log tab, and settings tab in location management
  READ_LOCATION_CONTACTS_SCOPE, // not used in the UI
  READ_LOCATION_ADDRESS_SCOPE, // not used in the UI ..... This seems like a mistake!!!!
  READ_LOCATION_MONITORING_SCOPE, // used for practice mode tab
  READ_MSP_LOCATIONS_SCOPE, // not used in UI
  READ_LOCATION_MSP_IDENTIFIERS_SCOPE, // not used in UI ..... This seems like a mistake!!!!
  READ_LOCATION_WORKFLOW_SCOPE, // not used in UI
  READ_LOCATION_SAFEWORD_SCOPE // used in the additional settings section in location settings
]
export const READ_LOCATION_MANAGEMENT_OTHER_SCOPES = [
  READ_JURISDICTION_RULES_SCOPE, // used for jurisdiction details tab
  JURISDICTION_RULES_TAB_SCOPE, // used to access the jurisdiction details tab
  READ_PERMITTING_COMMENTS_SCOPE,
  READ_PERMITS_SCOPE,
  READ_LOCATION_FEATURES_SCOPE // used for location features tab
]
export const WRITE_LOCATION_MANAGEMENT_SCOPES = [
  WRITE_LOCATIONS_SCOPE,
  WRITE_LOCATION_ADDRESS_SCOPE, // controls the edit button for the address fields in location settings
  WRITE_LOCATION_CONTACTS_SCOPE, // controls the edit button for the primary and secondary contact fields in location settings
  WRITE_LOCATION_DISPATCH_NOTES_SCOPE, // controls the edit button for the dispatch notes field in location settings
  WRITE_LOCATION_MONITORING_SCOPE, // controls the start and stop practice mode buttons in the practice mode tab
  WRITE_MONITORING_CERTIFICATE_SCOPE, // controls the send monitoring certificate button in the practice mode tab (but not the view certificate button)
  VALIDATE_ADDRESS_SCOPE, // not used in the UI but
  WRITE_PERMITTING_COMMENTS_SCOPE
]
export const WRITE_SENSITIVE_LOCATION_MANAGEMENT_SCOPES = [
  WRITE_LOCATION_MSP_IDENTIFIERS_SCOPE, // controls the ability to edit the site number field in location settings
  WRITE_LOCATION_TEMPLATE_ID_SCOPE, // controls the ability to edit the template ID field in location settings
  WRITE_LOCATION_SAFEWORD_SCOPE // controls the ability to edit the safeword field in location settings
]

// These are the location mgmt scopes that we need to request during login
export const ALL_LOCATION_MANAGEMENT_SCOPES = [
  ...READ_LOCATION_MANAGEMENT_SETTINGS_SCOPES,
  ...READ_LOCATION_MANAGEMENT_OTHER_SCOPES,
  ...WRITE_LOCATION_MANAGEMENT_SCOPES,
  ...WRITE_SENSITIVE_LOCATION_MANAGEMENT_SCOPES
]

export const PROFILE_SCOPES = [OPEN_ID_SCOPE, PROFILE_SCOPE, EMAIL_SCOPE] as const

// Landing Page Applet Visibility and Top Level Routing
export const ALARM_SMS_REVIEW_APPLET_SCOPES = READ_ALARM_SMS_REVIEW_SCOPES
export const ALARM_REVIEW_APPLET_SCOPES = [READ_LOCATIONS_SCOPE, READ_ALARMS_SCOPE]
export const LOCATION_FEATURES_APPLET_SCOPES = [READ_LOCATIONS_SCOPE, READ_LOCATION_ADDRESS_SCOPE, READ_LOCATION_FEATURES_SCOPE]
export const ENGAGEMENT_REVIEW_APPLET_SCOPES = [
  ERT_SCOPE,
  READ_LOCATIONS_SCOPE,
  ERT_READ_ENGAGEMENT_RECORDINGS,
  READ_DEVICES_SCOPE,
  READ_ENGAGEMENTS_SCOPE,
  READ_ALARMS_SCOPE,
  READ_ALARM_RECORDINGS_SCOPE,
  READ_SPECIALISTS_SCOPE
]
export const ADDRESS_SCHEDULER_APPLET_SCOPES = [READ_LOCATION_CHANGE_SCOPE]
export const MONITORING_MODERATION_APPLET_SCOPES = [READ_LOCATIONS_SCOPE, READ_DEVICES_SCOPE, READ_MODERATION_EVENTS_SCOPE]

/**
 * Roles needed by "Admin Review Tool - Developer"
 */
export const ALL_DEVTOOLS_SCOPES = [READ_LOCATIONS_SCOPE, DEVTOOLS_SCOPE]

// Need to dedupe this because noticed there were 17 duplicate scopes (at the time of implementing this dedupe)
export const ALL_SCOPES = Array.from(
  new Set([
    ...ALL_ALARM_REVIEW_SCOPES,
    ...ALL_PERMITTING_SCOPES,
    ...ENGAGEMENT_REVIEW_APPLET_SCOPES,
    ...ALL_LOCATION_CHANGE_SCOPES,
    ...ALL_MONITORING_MODERATION_SCOPES,
    ...ALL_LOCATION_MANAGEMENT_SCOPES,
    ...ALL_DEVTOOLS_SCOPES,
    ...PROFILE_SCOPES,
    WRITE_METRICS_SCOPE,
    READ_ALARM_SMS_INCIDENTS_SCOPE,
    ERT_INSPECT_MODE_SCOPE,
    ERT_QA_MODE_SCOPE,
    ERT_CUSTOMER_CARE_SCOPE,
    FAKE_SCOPE_FOR_TESTING
  ])
)
